import React, { useState } from 'react';
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Button,
  Alert,
  Card,
  CardContent,
  LinearProgress,
} from '@mui/material';

const questions = [
  {
    id: 1,
    question: "What percentage of adults aged 65+ fall each year?",
    options: ["10%", "15%", "25%", "35%"],
    correct: "25%",
    explanation: "According to CDC statistics, about 25% of older adults fall each year."
  },
  {
    id: 2,
    question: "Which age group has the highest risk of serious injury from falls?",
    options: ["65-70", "71-75", "76-80", "80+"],
    correct: "80+",
    explanation: "Adults over 80 have the highest risk due to increased frailty and health conditions."
  },
  {
    id: 3,
    question: "What percentage of falls result in serious injury requiring medical attention?",
    options: ["5-10%", "10-20%", "20-30%", "30-40%"],
    correct: "20-30%",
    explanation: "About 20-30% of falls result in moderate to severe injuries requiring medical care."
  },
  {
    id: 4,
    question: "How many fall-related emergency department visits occur annually?",
    options: ["1 million", "2 million", "3 million", "4 million"],
    correct: "3 million",
    explanation: "Approximately 3 million older adults are treated in emergency departments for fall injuries yearly."
  }
];

const StatisticsQuiz = ({ onComplete }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);

  const handleAnswer = (event) => {
    setAnswers({
      ...answers,
      [currentQuestion]: event.target.value
    });
    setShowExplanation(true);
  };

  const handleNext = () => {
    setShowExplanation(false);
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResult(true);
      onComplete(true);
    }
  };

  const calculateScore = () => {
    let score = 0;
    Object.keys(answers).forEach(questionIndex => {
      if (answers[questionIndex] === questions[questionIndex].correct) {
        score++;
      }
    });
    return (score / questions.length) * 100;
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResult(false);
    setShowExplanation(false);
    onComplete(false);
  };

  if (showResult) {
    const score = calculateScore();
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Quiz Complete!</Typography>
          <Typography variant="body1" gutterBottom>
            Your score: {score}%
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={score} 
            sx={{ my: 2, height: 10, borderRadius: 5 }}
          />
          <Button 
            variant="contained" 
            onClick={resetQuiz}
            sx={{ mt: 2 }}
          >
            Retake Quiz
          </Button>
        </CardContent>
      </Card>
    );
  }

  const question = questions[currentQuestion];

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Fall Prevention Statistics Quiz
        </Typography>
        <LinearProgress 
          variant="determinate" 
          value={(currentQuestion / questions.length) * 100} 
          sx={{ mb: 3, height: 10, borderRadius: 5 }}
        />
        
        <Typography variant="body1" gutterBottom>
          Question {currentQuestion + 1} of {questions.length}
        </Typography>
        
        <Typography variant="h6" gutterBottom>
          {question.question}
        </Typography>

        <FormControl component="fieldset">
          <RadioGroup
            value={answers[currentQuestion] || ''}
            onChange={handleAnswer}
          >
            {question.options.map((option, index) => (
              <FormControlLabel
                key={index}
                value={option}
                control={<Radio />}
                label={option}
              />
            ))}
          </RadioGroup>
        </FormControl>

        {showExplanation && (
          <Alert 
            severity={answers[currentQuestion] === question.correct ? "success" : "error"}
            sx={{ mt: 2, mb: 2 }}
          >
            {answers[currentQuestion] === question.correct ? "Correct! " : "Incorrect. "}
            {question.explanation}
          </Alert>
        )}

        <Button
          variant="contained"
          onClick={handleNext}
          disabled={!answers[currentQuestion]}
          sx={{ mt: 2 }}
        >
          {currentQuestion < questions.length - 1 ? 'Next Question' : 'Show Results'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default StatisticsQuiz;
